import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function PrivacyAccordion() {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const sections = [{
    id: 'information-collection',
    title: '1. Information We Collect',
    content: <>
          <p className="mb-4" data-id="element-836">
            We may collect the following types of personal information:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-837">
            <li className="flex items-start" data-id="element-838">
              <span className="mr-2" data-id="element-839">•</span>
              <span data-id="element-840">
                <strong data-id="element-841">Contact Information:</strong> Name, email address, phone
                number, business name, and postal address.
              </span>
            </li>
            <li className="flex items-start" data-id="element-842">
              <span className="mr-2" data-id="element-843">•</span>
              <span data-id="element-844">
                <strong data-id="element-845">Account Information:</strong> Login credentials, account
                preferences, and transaction history.
              </span>
            </li>
            <li className="flex items-start" data-id="element-846">
              <span className="mr-2" data-id="element-847">•</span>
              <span data-id="element-848">
                <strong data-id="element-849">Technical Information:</strong> IP address, browser
                type, device information, pages visited, and time spent on our
                website.
              </span>
            </li>
            <li className="flex items-start" data-id="element-850">
              <span className="mr-2" data-id="element-851">•</span>
              <span data-id="element-852">
                <strong data-id="element-853">Communications:</strong> Emails, chat messages, and
                other communications you have with us.
              </span>
            </li>
            <li className="flex items-start" data-id="element-854">
              <span className="mr-2" data-id="element-855">•</span>
              <span data-id="element-856">
                <strong data-id="element-857">Project Information:</strong> Details about your project
                requirements, goals, and preferences.
              </span>
            </li>
          </ul>
        </>
  }, {
    id: 'information-use',
    title: '2. How We Use Your Information',
    content: <>
          <p className="mb-4" data-id="element-858">
            We use your personal information for the following purposes:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-859">
            <li className="flex items-start" data-id="element-860">
              <span className="mr-2" data-id="element-861">•</span>
              <span data-id="element-862">To provide and improve our services</span>
            </li>
            <li className="flex items-start" data-id="element-863">
              <span className="mr-2" data-id="element-864">•</span>
              <span data-id="element-865">To communicate with you about your project or account</span>
            </li>
            <li className="flex items-start" data-id="element-866">
              <span className="mr-2" data-id="element-867">•</span>
              <span data-id="element-868">
                To send you relevant marketing communications (with your
                consent)
              </span>
            </li>
            <li className="flex items-start" data-id="element-869">
              <span className="mr-2" data-id="element-870">•</span>
              <span data-id="element-871">To process payments and manage billing</span>
            </li>
            <li className="flex items-start" data-id="element-872">
              <span className="mr-2" data-id="element-873">•</span>
              <span data-id="element-874">To comply with legal obligations</span>
            </li>
            <li className="flex items-start" data-id="element-875">
              <span className="mr-2" data-id="element-876">•</span>
              <span data-id="element-877">To analyze website usage and improve user experience</span>
            </li>
            <li className="flex items-start" data-id="element-878">
              <span className="mr-2" data-id="element-879">•</span>
              <span data-id="element-880">
                To protect our rights, property, or safety, and that of our
                users
              </span>
            </li>
          </ul>
        </>
  }, {
    id: 'legal-basis',
    title: '3. Legal Basis for Processing',
    content: <>
          <p className="mb-4" data-id="element-881">
            We process your personal information based on one or more of the
            following legal grounds:
          </p>
          <ul className="space-y-3 ml-4" data-id="element-882">
            <li className="flex items-start" data-id="element-883">
              <span className="mr-2" data-id="element-884">•</span>
              <span data-id="element-885">
                <strong data-id="element-886">Contract:</strong> Processing is necessary to fulfill
                our contractual obligations to you.
              </span>
            </li>
            <li className="flex items-start" data-id="element-887">
              <span className="mr-2" data-id="element-888">•</span>
              <span data-id="element-889">
                <strong data-id="element-890">Legitimate Interests:</strong> Processing is necessary
                for our legitimate business interests, provided those interests
                don't override your rights.
              </span>
            </li>
            <li className="flex items-start" data-id="element-891">
              <span className="mr-2" data-id="element-892">•</span>
              <span data-id="element-893">
                <strong data-id="element-894">Consent:</strong> You have given us permission to
                process your data for specific purposes.
              </span>
            </li>
            <li className="flex items-start" data-id="element-895">
              <span className="mr-2" data-id="element-896">•</span>
              <span data-id="element-897">
                <strong data-id="element-898">Legal Obligation:</strong> Processing is necessary to
                comply with our legal obligations.
              </span>
            </li>
          </ul>
        </>
  }, {
    id: 'data-retention',
    title: '4. Data Retention',
    content: <>
          <p className="mb-4" data-id="element-899">
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this Privacy Policy, unless a
            longer retention period is required or permitted by law. When
            determining how long to keep your data, we consider:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-900">
            <li className="flex items-start" data-id="element-901">
              <span className="mr-2" data-id="element-902">•</span>
              <span data-id="element-903">
                The amount, nature, and sensitivity of the personal information
              </span>
            </li>
            <li className="flex items-start" data-id="element-904">
              <span className="mr-2" data-id="element-905">•</span>
              <span data-id="element-906">
                The potential risk of harm from unauthorized use or disclosure
              </span>
            </li>
            <li className="flex items-start" data-id="element-907">
              <span className="mr-2" data-id="element-908">•</span>
              <span data-id="element-909">The purposes for which we process the data</span>
            </li>
            <li className="flex items-start" data-id="element-910">
              <span className="mr-2" data-id="element-911">•</span>
              <span data-id="element-912">
                Whether we can achieve those purposes through other means
              </span>
            </li>
            <li className="flex items-start" data-id="element-913">
              <span className="mr-2" data-id="element-914">•</span>
              <span data-id="element-915">Legal, regulatory, and contractual requirements</span>
            </li>
          </ul>
        </>
  }, {
    id: 'cookies',
    title: '5. Cookies and Similar Technologies',
    content: <>
          <p className="mb-4" data-id="element-916">
            We use cookies and similar technologies to enhance your browsing
            experience, analyze website traffic, and personalize content.
            Cookies are small text files stored on your device that help us
            recognize you when you return to our website.
          </p>
          <p className="mb-4 font-semibold" data-id="element-917">Types of cookies we use:</p>
          <ul className="space-y-3 ml-4" data-id="element-918">
            <li className="flex items-start" data-id="element-919">
              <span className="mr-2" data-id="element-920">•</span>
              <span data-id="element-921">
                <strong data-id="element-922">Essential Cookies:</strong> Required for the website to
                function properly.
              </span>
            </li>
            <li className="flex items-start" data-id="element-923">
              <span className="mr-2" data-id="element-924">•</span>
              <span data-id="element-925">
                <strong data-id="element-926">Analytical/Performance Cookies:</strong> Help us
                understand how visitors interact with our website.
              </span>
            </li>
            <li className="flex items-start" data-id="element-927">
              <span className="mr-2" data-id="element-928">•</span>
              <span data-id="element-929">
                <strong data-id="element-930">Functionality Cookies:</strong> Remember your
                preferences and settings.
              </span>
            </li>
            <li className="flex items-start" data-id="element-931">
              <span className="mr-2" data-id="element-932">•</span>
              <span data-id="element-933">
                <strong data-id="element-934">Targeting/Advertising Cookies:</strong> Track your
                browsing habits to deliver relevant advertising (with your
                consent).
              </span>
            </li>
          </ul>
          <p className="mt-4" data-id="element-935">
            You can manage cookie preferences through your browser settings.
            Please note that disabling certain cookies may affect the
            functionality of our website.
          </p>
        </>
  }, {
    id: 'data-sharing',
    title: '6. Data Sharing and Transfers',
    content: <>
          <p className="mb-4" data-id="element-936">We may share your personal information with:</p>
          <ul className="space-y-3 ml-4" data-id="element-937">
            <li className="flex items-start" data-id="element-938">
              <span className="mr-2" data-id="element-939">•</span>
              <span data-id="element-940">
                <strong data-id="element-941">Service Providers:</strong> Third parties who perform
                services on our behalf (e.g., hosting providers, payment
                processors).
              </span>
            </li>
            <li className="flex items-start" data-id="element-942">
              <span className="mr-2" data-id="element-943">•</span>
              <span data-id="element-944">
                <strong data-id="element-945">Professional Advisors:</strong> Lawyers, accountants,
                and insurers when necessary.
              </span>
            </li>
            <li className="flex items-start" data-id="element-946">
              <span className="mr-2" data-id="element-947">•</span>
              <span data-id="element-948">
                <strong data-id="element-949">Regulatory Authorities:</strong> Government bodies or
                law enforcement when required by law.
              </span>
            </li>
            <li className="flex items-start" data-id="element-950">
              <span className="mr-2" data-id="element-951">•</span>
              <span data-id="element-952">
                <strong data-id="element-953">Business Partners:</strong> With your consent, we may
                share your information with trusted partners.
              </span>
            </li>
          </ul>
          <p className="mt-4" data-id="element-954">
            When we transfer your data outside the UK or European Economic Area
            (EEA), we ensure appropriate safeguards are in place to protect your
            information in accordance with GDPR requirements.
          </p>
        </>
  }, {
    id: 'your-rights',
    title: '7. Your Rights',
    content: <>
          <p className="mb-4" data-id="element-955">
            Under GDPR, you have the following rights regarding your personal
            data:
          </p>
          <ul className="space-y-3 ml-4" data-id="element-956">
            <li className="flex items-start" data-id="element-957">
              <span className="mr-2" data-id="element-958">•</span>
              <span data-id="element-959">
                <strong data-id="element-960">Access:</strong> Request a copy of your personal
                information.
              </span>
            </li>
            <li className="flex items-start" data-id="element-961">
              <span className="mr-2" data-id="element-962">•</span>
              <span data-id="element-963">
                <strong data-id="element-964">Rectification:</strong> Request correction of inaccurate
                or incomplete data.
              </span>
            </li>
            <li className="flex items-start" data-id="element-965">
              <span className="mr-2" data-id="element-966">•</span>
              <span data-id="element-967">
                <strong data-id="element-968">Erasure:</strong> Request deletion of your personal
                information in certain circumstances.
              </span>
            </li>
            <li className="flex items-start" data-id="element-969">
              <span className="mr-2" data-id="element-970">•</span>
              <span data-id="element-971">
                <strong data-id="element-972">Restriction:</strong> Request limitation of processing
                in certain circumstances.
              </span>
            </li>
            <li className="flex items-start" data-id="element-973">
              <span className="mr-2" data-id="element-974">•</span>
              <span data-id="element-975">
                <strong data-id="element-976">Data Portability:</strong> Request transfer of your data
                to another organization.
              </span>
            </li>
            <li className="flex items-start" data-id="element-977">
              <span className="mr-2" data-id="element-978">•</span>
              <span data-id="element-979">
                <strong data-id="element-980">Objection:</strong> Object to processing based on
                legitimate interests or direct marketing.
              </span>
            </li>
            <li className="flex items-start" data-id="element-981">
              <span className="mr-2" data-id="element-982">•</span>
              <span data-id="element-983">
                <strong data-id="element-984">Withdrawal of Consent:</strong> Withdraw consent where
                processing is based on consent.
              </span>
            </li>
          </ul>
          <p className="mt-4" data-id="element-985">
            To exercise these rights, please contact us using the details
            provided in the "Contact Us" section.
          </p>
        </>
  }, {
    id: 'data-security',
    title: '8. Data Security',
    content: <>
          <p className="mb-4" data-id="element-986">
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-987">
            <li className="flex items-start" data-id="element-988">
              <span className="mr-2" data-id="element-989">•</span>
              <span data-id="element-990">Encryption of sensitive data</span>
            </li>
            <li className="flex items-start" data-id="element-991">
              <span className="mr-2" data-id="element-992">•</span>
              <span data-id="element-993">Regular security assessments</span>
            </li>
            <li className="flex items-start" data-id="element-994">
              <span className="mr-2" data-id="element-995">•</span>
              <span data-id="element-996">Access controls and authentication</span>
            </li>
            <li className="flex items-start" data-id="element-997">
              <span className="mr-2" data-id="element-998">•</span>
              <span data-id="element-999">Secure network architecture</span>
            </li>
            <li className="flex items-start" data-id="element-1000">
              <span className="mr-2" data-id="element-1001">•</span>
              <span data-id="element-1002">Regular backups</span>
            </li>
            <li className="flex items-start" data-id="element-1003">
              <span className="mr-2" data-id="element-1004">•</span>
              <span data-id="element-1005">Staff training on data protection</span>
            </li>
          </ul>
          <p className="mt-4" data-id="element-1006">
            While we strive to protect your personal information, no method of
            transmission over the Internet or electronic storage is 100% secure.
            We encourage you to use caution when sharing information online.
          </p>
        </>
  }, {
    id: 'contact',
    title: '9. Contact Us',
    content: <>
          <p className="mb-4" data-id="element-1007">
            If you have any questions, concerns, or requests regarding this
            Privacy & Cookie Policy or our data practices, please contact us at:
          </p>
          <p className="mb-2" data-id="element-1008">
            Email:{' '}
            <a href="mailto:privacy@bright-pixxel.com" className="text-[#0C1B26] hover:underline" data-id="element-1009">
              privacy@bright-pixxel.com
            </a>
          </p>
          <p className="mb-2" data-id="element-1010">
            Address: Bright Pixxel Studio, 123 Web Street, London, UK
          </p>
          <p className="mb-4" data-id="element-1011">Phone: +44 (0) 123 456 7890</p>
          <p data-id="element-1012">
            You have the right to lodge a complaint with the Information
            Commissioner's Office (ICO) if you believe we have not handled your
            personal information in accordance with data protection law.
          </p>
        </>
  }];
  const toggleSection = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };
  return <div className="space-y-4" data-id="element-1013">
      {sections.map(section => <div key={section.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-1014">
          <button onClick={() => toggleSection(section.id)} className={`w-full p-4 flex items-center justify-between text-left transition-colors ${openSection === section.id ? 'bg-[#0C1B26]/10 dark:bg-[#0C1B26]/20' : 'bg-white dark:bg-[#1E1E1E]'}`} data-id="element-1015">
            <h3 className="text-lg font-semibold" data-id="element-1016">{section.title}</h3>
            <ChevronDownIcon size={20} className={`transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} data-id="element-1017" />
          </button>
          <AnimatePresence data-id="element-1018">
            {openSection === section.id && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-1019">
                <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-1020">
                  <div className="text-gray-600 dark:text-gray-300" data-id="element-1021">
                    {section.content}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}